import React, { useEffect, useState } from "react";
import { Link, PageProps, graphql } from "gatsby";
import HeaderFooter from "@/layouts/header-footer";
import { h1, h2, h4, p, pl } from "@components/ui/typography/typography.module.css";
import AvatarDetails from "@/components/avatar-detail";
import ReactMarkdown from "react-markdown";
import {
  articleHeader,
  tableOfContentsAside,
  tableOfContentsItem,
  authorContainer,
  blogPostCardImage,
  stickyTop,
  markdownContent,
  darkGrey,
  footerAvatar
} from "./template.module.css";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import Chip from "@/components/ui/chip";
import { container } from "@styles/common-styles.module.css";
import Section from "@/layouts/section";
import TextLabel from "@/components/ui/text-label";
import DualColumn from "@/components/ui/dual-column";
import Button from "@/components/ui/button";
import { cn, kebabCase } from "@/utils";
import SvgIcon from "@/components/ui/svg-icons";
import rehypeRaw from "rehype-raw";
import HomepageCta from "@/components/cta/home-page-cta";
import BlogHeadings from "@/components/blog/blog-headings";
import { Helmet } from "react-helmet";

interface ViewAllBlogsProps extends React.HTMLProps<HTMLButtonElement> {}

const ViewAllBlogs: React.FC<ViewAllBlogsProps> = ({ className, disabled }) => (
  <Link to="/blog">
    <Button
      disabled={disabled}
      variant="text"
      className={cn("items-center justify-between gap-4 group", className)}
    >
      View all
      <SvgIcon
        name="arrow"
        className={`w-6 h-6 ${
          !disabled ? "will-change-transform transition-transform group-hover:translate-x-1" : ""
        }`}
      />
    </Button>
  </Link>
);
interface singleBlogPost {
  ctaTitle: string;
  ctaDescription: string;
  ctaBtnText: string;
}

export interface BlogPost {
  seo: {
    seoTitle: string;
    seoDescription: string;
  };
  author: {
    avatar: {
      alternativeText: string;
      localFile: {
        childImageSharp: {
          gatsbyImageData: IGatsbyImageData;
        };
      };
    };
    facebookLink: string;
    linkedInLink: string;
    twitterLink: string;
    role: string;
    name: string;
    about: string;
  };
  id: string;
  title: string;
  description: string;
  readTime: string;
  slug: {
    slug: string;
  };
  preTitleTopic: string;
  heroImage: {
    localFile: {
      childImageSharp: {
        gatsbyImageData: IGatsbyImageData;
      };
    };
    alternativeText: string;
  };
  content: {
    data: {
      content: string;
    };
  };
  tags: {
    tag: string;
  }[];
}

export interface BlogPostData {
  strapiBlog: BlogPost;
  allStrapiBlog: {
    nodes: BlogPost[];
  };
  allStrapiSingleBlogPage: {
    nodes: singleBlogPost[];
  };
}

const BlogPostPage: React.FC<PageProps> = ({ data }) => {
  const [titles, setTitles] = useState<string[]>([]);
  const [linkUnderlineTop, setLinkUnderlineTop] = useState(0);
  const [activeLinkIndex, setActiveLinkIndex] = useState(0);
  const [canonicalUrl, setCanonicalUrl] = useState("");
  const blogPost = (data as BlogPostData).strapiBlog;
  const blogPostCta = (data as BlogPostData).allStrapiSingleBlogPage.nodes;
  const blogs = ((data as BlogPostData).allStrapiBlog.nodes || [])
    .filter((blog) => blog.id !== blogPost.id)
    .slice(0, 3);

  const handleBlogHeadingClick = (index: number) => {
    setActiveLinkIndex(index);
    document
      .querySelectorAll(".markdown-content > h2")
      [index].scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      setCanonicalUrl(window.location.href);
    }
    const elementTitles: string[] = [];
    let timer: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      document.querySelectorAll(".markdown-content > h2").forEach((section) => {
        if (section.getBoundingClientRect().top < 200) {
          const index = Number(section.getAttribute("data-section"));
          setActiveLinkIndex(isNaN(index) ? 0 : index);
        }
      });
    };
    handleScroll();

    function setLinkPosition() {
      timer = setTimeout(() => {
        const currentTab = document.querySelectorAll("#toc > li")[activeLinkIndex];
        if (currentTab) {
          setLinkUnderlineTop(
            (currentTab as HTMLElement).offsetTop + (currentTab.clientHeight / 2 - 8) ?? 0
          );
        }
      }, 100);
    }
    document.querySelectorAll(".markdown-content > h2").forEach((element, i) => {
      const text = element.textContent || "";

      element.setAttribute("data-section", String(i));
      elementTitles.push(text);
    });

    setTitles(elementTitles);
    setLinkPosition();

    document.addEventListener("scroll", setLinkPosition);
    document.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("scroll", setLinkPosition);
      document.removeEventListener("scroll", handleScroll);
      clearTimeout(timer);
    };
  }, [activeLinkIndex]);

  return (
    <>
      <Helmet>
        <title>{blogPost.seo.seoTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={blogPost.seo.seoDescription} />
        {/* <meta name="robots" content={blogPost.seo.noIndex} /> */}
      </Helmet>
      <HeaderFooter>
        <article className={container}>
          <header className={articleHeader}>
            {!!blogPost.preTitleTopic && !!blogPost.readTime && (
              <p className={`${p}`}>
                {blogPost.preTitleTopic} - {blogPost.readTime} read
              </p>
            )}
            <h1 className={`${h1} mt-6`}>{blogPost.title}</h1>
            {!!blogPost.description.trim() && (
              <p className={`${pl} mt-6 text-dark`}>{blogPost.description}</p>
            )}
            <div className="md:flex md:justify-center">
              <AvatarDetails
                className="mt-8 md:mt-12 text-left"
                avatar={blogPost.author.avatar.localFile.childImageSharp.gatsbyImageData}
                altText={blogPost.author.avatar.alternativeText}
                name={blogPost.author.name}
                subText={blogPost.author.role}
              />
            </div>
          </header>
          <BlogHeadings
            titles={titles}
            activeLinkIndex={activeLinkIndex}
            setActiveLinkIndex={handleBlogHeadingClick}
          />
          <section className="mt-16 md:mt-20 flex justify-center">
            <GatsbyImage
              image={blogPost.heroImage.localFile.childImageSharp.gatsbyImageData}
              alt={blogPost.heroImage.alternativeText}
              className="overflow-hidden rounded-lg"
            />
          </section>
          <div className="flex flex-col md:flex-row mt-28 md:mt-32 gap-20 relative">
            <aside className={`${tableOfContentsAside}`}>
              <div className={`${stickyTop}`}>
                {titles && titles.length > 0 && (
                  <div className="flex max-w-full">
                    <div className="flex shrink-0 border-l border-dark justify-center w-8 relative">
                      <span
                        className="absolute w-4 h-4 rounded-full bg-primary top-4"
                        style={{ top: linkUnderlineTop }}
                      />
                    </div>
                    <ul id="toc">
                      {titles.map((t, i) => (
                        <li
                          key={i}
                          className={`${tableOfContentsItem} ${activeLinkIndex === i ? "text-primary" : darkGrey} text-lg`}
                        >
                          <button
                            className="hover:text-primary text-left"
                            onClick={() => handleBlogHeadingClick(i)}
                          >
                            {t}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="text-primary gap-6 w-full mt-10 hidden md:flex">
                  <svg
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.9047 11.4689L21.3513 2.99219H19.5873L13.1187 10.3509L7.956 2.99219H2L9.80867 14.1209L2 23.0089H3.764L10.5907 15.2362L16.044 23.0089H22M4.40067 4.29485H7.11067L19.586 21.7702H16.8753"
                      fill="currentColor"
                    />
                  </svg>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.32946 8.64076H6.48587V22H2.32946V8.64076ZM4.40869 2C5.73685 2 6.81532 3.07848 6.81532 4.40732C6.81532 5.73616 5.73685 6.81532 4.40869 6.81532C3.08054 6.81532 2 5.73685 2 4.40732C2 3.07779 3.07504 2 4.40869 2ZM9.09058 22H13.2374V15.3929C13.2374 13.65 13.5668 11.9615 15.7272 11.9615C17.8876 11.9615 17.8849 13.9547 17.8849 15.5037V22H22V14.6735C22 11.0756 21.2592 8.30924 17.0554 8.30924C15.0353 8.30924 13.681 9.4166 13.1273 10.4669H13.0702V8.64076H9.09058V22Z"
                      fill="currentColor"
                    />
                  </svg>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.4156 23V12.9644H16.7844L17.2897 9.05337H13.4156V6.55674C13.4156 5.42401 13.7299 4.65272 15.3544 4.65272H17.4261V1.1543C16.4238 1.04799 15.4164 0.996543 14.4085 1.00018C11.4249 0.999498 9.37908 2.82236 9.37908 6.16939V9.05337H6V12.9644H9.37908V23H13.4156Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
              </div>
            </aside>
            <main className="mt-20 md:mt-0 flex flex-1 flex-col grow blog-post">
              <ReactMarkdown
                className={` markdown-content ${markdownContent}`}
                // @ts-expect-error: third party plugin issue
                rehypePlugins={[rehypeRaw]}
              >
                {blogPost.content.data.content}
              </ReactMarkdown>
              <footer>
                <div className={authorContainer}>
                  <div
                    className={`${footerAvatar} flex w-20 h-20 grow-0 rounded-full overflow-hidden`}
                  >
                    <GatsbyImage
                      image={blogPost.author.avatar.localFile.childImageSharp.gatsbyImageData}
                      alt={blogPost.author.avatar.alternativeText}
                    />
                  </div>
                  <div className="flex flex-col grow bg-green-300">
                    <p className={`${h4} mt-8 md:mt-0 `}>Written by {blogPost.author.name}</p>
                    <p className={`${pl} mt-2`}>{blogPost.author.about}</p>
                    <div className="flex gap-6 text-primary mt-8 md:-mt-6">
                      <a href={blogPost.author.twitterLink} target="_blank">
                        <svg
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13.9047 11.4689L21.3513 2.99219H19.5873L13.1187 10.3509L7.956 2.99219H2L9.80867 14.1209L2 23.0089H3.764L10.5907 15.2362L16.044 23.0089H22M4.40067 4.29485H7.11067L19.586 21.7702H16.8753"
                            fill="currentColor"
                          />
                        </svg>
                      </a>
                      <a href={blogPost.author.linkedInLink} target="_blank">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2.32946 8.64076H6.48587V22H2.32946V8.64076ZM4.40869 2C5.73685 2 6.81532 3.07848 6.81532 4.40732C6.81532 5.73616 5.73685 6.81532 4.40869 6.81532C3.08054 6.81532 2 5.73685 2 4.40732C2 3.07779 3.07504 2 4.40869 2ZM9.09058 22H13.2374V15.3929C13.2374 13.65 13.5668 11.9615 15.7272 11.9615C17.8876 11.9615 17.8849 13.9547 17.8849 15.5037V22H22V14.6735C22 11.0756 21.2592 8.30924 17.0554 8.30924C15.0353 8.30924 13.681 9.4166 13.1273 10.4669H13.0702V8.64076H9.09058V22Z"
                            fill="currentColor"
                          />
                        </svg>
                      </a>
                      <a href={blogPost.author.facebookLink} target="_blank">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13.4156 23V12.9644H16.7844L17.2897 9.05337H13.4156V6.55674C13.4156 5.42401 13.7299 4.65272 15.3544 4.65272H17.4261V1.1543C16.4238 1.04799 15.4164 0.996543 14.4085 1.00018C11.4249 0.999498 9.37908 2.82236 9.37908 6.16939V9.05337H6V12.9644H9.37908V23H13.4156Z"
                            fill="currentColor"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="mt-20">
                  <p className={`${h2} text-dark`}>Tags</p>
                  <div className="mt-6">
                    {blogPost.tags.map(({ tag }, i) => (
                      <Link key={i} to={`/blog/category/${kebabCase(tag)}`}>
                        <Chip className="text-dark">{tag}</Chip>
                      </Link>
                    ))}
                  </div>
                </div>
              </footer>
            </main>
          </div>
        </article>
        {blogs.length > 0 && (
          <Section>
            <DualColumn
              firstColumn={
                <>
                  <TextLabel>Other blogs</TextLabel>
                  <h3 className={`${h1} mt-6`}>You may also like</h3>
                </>
              }
              firstColumnClassName="flex-col"
              lastColumn={
                <div className="flex w-full justify-end items-end">
                  <div>
                    <ViewAllBlogs className="hidden md:inline-flex" />
                  </div>
                </div>
              }
            />
            <hr className="h-[2px] bg-dark mt-8 md:mt-10" />
            <div className="mt-16 flex flex-col md:flex-row gap-8">
              {blogs.map((blog) => (
                <div key={blog.id} className="md:w-1/3">
                  <Link to={`/blog/${blog.slug.slug}`} className="group">
                    <div className="rounded-2xl overflow-hidden">
                      <GatsbyImage
                        image={blog.heroImage.localFile.childImageSharp.gatsbyImageData}
                        alt={blog.heroImage?.alternativeText || "Hero image"}
                        className={blogPostCardImage}
                      />
                    </div>
                    <div className="mt-6 md:mt-8">
                      <span className="uppercase">{blog.tags.map((t) => t.tag).join(" ")}</span>
                      <h4 className={`${h4} mt-2 group-hover:text-primary`}>{blog.title}</h4>
                      <AvatarDetails
                        className="mt-6 md:mt-8"
                        name={blog.author.name}
                        avatar={blog.author.avatar.localFile.childImageSharp.gatsbyImageData}
                        alt={blog.author.avatar.alternativeText}
                        subText={`${blog.readTime} read`}
                      />
                    </div>
                  </Link>
                </div>
              ))}
            </div>
            <ViewAllBlogs className="inline-flex mt-16 md:m-0 md:hidden" />
          </Section>
        )}
        {blogPostCta.map((cta) => (
          <HomepageCta title={cta.ctaTitle} description={cta.ctaDescription} cta={cta.ctaBtnText} />
        ))}
      </HeaderFooter>
    </>
  );
};
export const query = graphql`
  query BlogPost($slug: String) {
    allStrapiSingleBlogPage {
      nodes {
        ctaTitle
        ctaDescription
        ctaBtnText
      }
    }
    strapiBlog(slug: { slug: { eq: $slug } }) {
      seo {
        seoTitle
        seoDescription
      }
      author {
        avatar {
          alternativeText
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        facebookLink
        linkedInLink
        twitterLink
        role
        name
        about
      }
      id
      title
      description
      readTime
      preTitleTopic
      heroImage {
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
        alternativeText
      }
      content {
        data {
          content
        }
      }
      tags {
        tag
      }
    }
    allStrapiBlog {
      nodes {
        id
        heroImage {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
          alternativeText
        }
        title
        readTime
        slug {
          slug
        }
        tags {
          tag
        }
        author {
          name
          avatar {
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
            alternativeText
          }
        }
      }
    }
  }
`;
export default BlogPostPage;
